export type ChatbotState = {
  status: "DISABLED" | "HIDDEN" | "GIGA_CHAT_VISIBLE" | "PROACTIVE_CHAT_VISIBLE";
  triggerType: string | null;
  proactiveModalViewed?: { [page: string]: boolean };
  gigaEventTriggered?: { [page: string]: boolean };
  vasCounter: number;
  productCounter: number;
  vasTriggeredPages: { [page: string]: boolean };
  productTriggeredPages: { [page: string]: boolean };
  proactiveModalContent?: ProactiveModalContent | ProactiveModalContentByTrigger;
};

export interface ProactiveModalContent {
  darkMode?: boolean;
  icon?: boolean;
  heading?: {
    copy: string;
  };
  subheading?: {
    copy: string;
  };
  steps?: string[];
  helperText?: string;
  cta?: {
    copy: string;
    backgroundColor?: "red" | "electricity";
  };
}

/**
 * Per-trigger modal copy. `default` is required as the fallback when the
 * active trigger has no override. Keys mirror the camelCase form used by
 * `ChatbotTriggers` in useChatbotMonarch (idle, backButton, vasUncertainty,
 * productUncertainty, formError).
 */
export interface ProactiveModalContentByTrigger {
  default: ProactiveModalContent;
  idle?: ProactiveModalContent;
  backButton?: ProactiveModalContent;
  vasUncertainty?: ProactiveModalContent;
  productUncertainty?: ProactiveModalContent;
  formError?: ProactiveModalContent;
}

export const isProactiveModalContentByTrigger = (
  value: ProactiveModalContent | ProactiveModalContentByTrigger | undefined
): value is ProactiveModalContentByTrigger => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return false;
  if (!("default" in value)) return false;
  const defaultValue = (value as ProactiveModalContentByTrigger).default;
  return defaultValue != null && typeof defaultValue === "object" && !Array.isArray(defaultValue);
};

export enum ChatbotActionTypes {
  SET_CHATBOT_STATUS = "chatbot/set-status",
  SET_CHATBOT_TRIGGER_TYPE = "chatbot/set-trigger-type",
  SET_PROACTIVE_MODAL_VIEWED_PAGE = "chatbot/set-proactive-modal-viewed-page",
  SET_GIGA_EVENT_TRIGGERED_PAGE = "chatbot/set-giga-event-triggered-page",
  SET_PROACTIVE_MODAL_CONTENT = "chatbot/set-proactive-modal-content",
  INCREMENT_VAS_COUNTER = "chatbot/increment-vas-counter",
  INCREMENT_PRODUCT_COUNTER = "chatbot/increment-product-counter",
  MARK_VAS_TRIGGERED_PAGE = "chatbot/mark-vas-triggered-page",
  MARK_PRODUCT_TRIGGERED_PAGE = "chatbot/mark-product-triggered-page",
}

export interface SetChatbotStatusAction {
  type: ChatbotActionTypes.SET_CHATBOT_STATUS;
  status: ChatbotState["status"];
}

export interface SetChatbotTriggerTypeAction {
  type: ChatbotActionTypes.SET_CHATBOT_TRIGGER_TYPE;
  triggerType: ChatbotState["triggerType"];
}

export interface SetProactiveModalViewedPageAction {
  type: ChatbotActionTypes.SET_PROACTIVE_MODAL_VIEWED_PAGE;
  page: string;
  viewed: boolean;
}

export interface SetGigaEventTriggeredPageAction {
  type: ChatbotActionTypes.SET_GIGA_EVENT_TRIGGERED_PAGE;
  page: string;
  viewed: boolean;
}

export interface SetProactiveModalContentAction {
  type: ChatbotActionTypes.SET_PROACTIVE_MODAL_CONTENT;
  proactiveModalContent: ProactiveModalContent | ProactiveModalContentByTrigger;
}

export interface IncrementVasCounterAction {
  type: ChatbotActionTypes.INCREMENT_VAS_COUNTER;
}

export interface IncrementProductCounterAction {
  type: ChatbotActionTypes.INCREMENT_PRODUCT_COUNTER;
}

export interface MarkVasTriggeredPageAction {
  type: ChatbotActionTypes.MARK_VAS_TRIGGERED_PAGE;
  page: string;
}

export interface MarkProductTriggeredPageAction {
  type: ChatbotActionTypes.MARK_PRODUCT_TRIGGERED_PAGE;
  page: string;
}

export type ChatbotActions =
  | SetChatbotStatusAction
  | SetChatbotTriggerTypeAction
  | SetProactiveModalViewedPageAction
  | SetGigaEventTriggeredPageAction
  | SetProactiveModalContentAction
  | IncrementVasCounterAction
  | IncrementProductCounterAction
  | MarkVasTriggeredPageAction
  | MarkProductTriggeredPageAction;
